import {useSettings} from "@/store/settings";
import {createPinia} from "pinia";
import {BerWowSettings} from "./types/global";

import App from "./App.vue";
import PrimeVue from "primevue/config";
import "primeicons/primeicons.css";

import "mapbox-gl/dist/mapbox-gl.css";
import {on} from "@/helpers";
import Tooltip from "primevue/tooltip";
import ToastService from "primevue/toastservice";
import ConfirmationService from "primevue/confirmationservice";
import { safeHtmlDirective } from "@/directives/safe-html";

import "intro.js/introjs.css";
import {App as VueApp, createApp} from "vue";
import {createMemoryHistory, createRouter, createWebHistory, Router, RouteRecordRaw,} from "vue-router";

import RecommendationsResult from "@/components/result/recommendations/RecommendationsResult.vue";
import FinancialDetail from "@/components/result/financial-detail/FinancialDetail.vue";
import CarbonFootprint from "@/components/result/carbon-footprint/CarbonFootprint.vue";
import BERMap from "@/components/result/ber-map/BERMap.vue";
import CostsAndSavings from "@/components/result/costs-and-savings/CostsAndSavings.vue";
import SelectionsPage from "@/components/SelectionsPage.vue";

import {configureAxios} from "@/service/axios";
import {getI18n, setLocaleOverwrites} from "./helpers/locale";
import AnalysisTab from "@/components/result/analysis/AnalysisTab.vue";
import BrPassport from "@/components/result/br-passport/BrPassport.vue";

import "@/assets/styles/theme.scss";

export default class BerWowRecommender {
    instance: VueApp<Element>;
    settings: BerWowSettings;
    on: (event: string, listener: any) => void = on;

    resultPageRouting: RouteRecordRaw[] = [
        {name: "selections-page", path: "/", component: SelectionsPage},
        {
            name: "recommendations",
            path: "/recommendations",
            component: RecommendationsResult,
        },
        {
            name: "financial-detail",
            path: "/financial-detail",
            component: FinancialDetail,
        },
        {
            name: "carbon-footprint",
            path: "/carbon-footprint",
            component: CarbonFootprint,
        },
        {name: "ber-map", path: "/ber-map", component: BERMap},
        {
            name: "costs-and-savings",
            path: "/costs-and-savings",
            component: CostsAndSavings,
        },
        {name: "analysis", path: "/analysis", component: AnalysisTab},
        {name: "brp", path: "/brp", component: BrPassport},
    ];

    router: Router | undefined = undefined;

    constructor(settings: BerWowSettings) {
        this.settings = settings;
        this.instance = createApp(App);
        this.initApp(settings);
    }

    private async initApp(settings: BerWowSettings) {
        if (settings.autoaddressKey) {
            this.loadAutoaddressDependencies();
        }

        this.router = createRouter({
            history: settings.useWebHistory
                ? createWebHistory(
                    window.location.pathname.replace(
                        new RegExp(
                            `(${this.resultPageRouting
                                .map((route) => route.path)
                                .filter((path) => path !== "/")
                                .join("|")})$`,
                            "g"
                        ),
                        ""
                    )
                )
                : createMemoryHistory(),
            routes: this.resultPageRouting,
        });

        const pathSegments = window.location.pathname.split("/").filter(Boolean);
        if (pathSegments.length && settings.useWebHistory) {
            const lastSegment = pathSegments.pop();
            const isRoute = this.router
                .getRoutes()
                .some((route) => route.path === "/" + lastSegment);
            if (isRoute) {
                const path = pathSegments.join("/").replace(/\/+/g, "/");
                window.location.href = path.length
                    ? `${window.location.origin}/${path}/`
                    : `${window.location.origin}/`;
            }
        }

        this.instance
            .use(getI18n())
            .use(PrimeVue)
            .use(this.router)
            .directive("tooltip", Tooltip)
            .directive("safe-html", safeHtmlDirective)
            .use(ConfirmationService)
            .use(ToastService)
            .use(createPinia());

        if (settings.locale) {
            await setLocaleOverwrites(settings.locale);
        }
        this.instance.mount("#" + settings.mountElementId);

        configureAxios();
        useSettings().setSettings(this.settings);
    }

    private async loadAutoaddressDependencies(): Promise<void> {
        return new Promise((resolve, reject) => {
            // Autoaddress css
            const file = document.createElement("link");
            file.rel = "stylesheet";
            file.href =
                "https://integrations.autoaddress.com/jscontrol/autoaddress-style.css";
            document.head.appendChild(file);

            // Autoaddress JS control
            const scriptElement = document.createElement("script");
            scriptElement.setAttribute(
                "src",
                "https://integrations.autoaddress.com/jscontrol/autoaddress.min.js"
            );
            scriptElement.onload = () => resolve();
            scriptElement.onerror = (err) => reject(err);
            document.head.appendChild(scriptElement);
        });
    }
}
